import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Mail, Phone, MessageCircle } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { FadeUp, RevealMaskScroll } from "../components/Reveal";
import { useSiteContent } from "../context/SiteContentContext";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const site = useSiteContent();
  const t = (k, f) => site.text(k, f);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please fill all fields"); return; }
    toast.success("Message received — our atelier will be in touch.");
    setForm({ name: "", email: "", message: "" });
  };

  const rows = [
    [MapPin, t("contact_addr_label", "Flagship Atelier"), t("contact_addr_value", "14 Kala Ghoda, Fort, Mumbai 400001")],
    [Mail, t("contact_email_label", "Email"), t("contact_email_value", "care@amraiya.com")],
    [Phone, t("contact_phone_label", "Telephone"), t("contact_phone_value", "+91 22 4000 1200")],
    [MessageCircle, t("contact_whatsapp_label", "WhatsApp"), t("contact_whatsapp_value", "Chat with a stylist, 10am–8pm IST")],
  ];

  return (
    <div className="pt-20" data-testid="contact-page">
      <div className="px-5 md:px-10 pt-16 pb-12 max-w-[1400px] mx-auto">
        <FadeUp><p className="overline text-gold-deep mb-4">{t("contact_kicker", "Get in touch")}</p></FadeUp>
        <h1 className="font-serif text-6xl md:text-8xl tracking-tight leading-none">
          <RevealMaskScroll>{t("contact_title", "Contact us")}</RevealMaskScroll>
        </h1>
      </div>

      <div className="px-5 md:px-10 max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-16 pb-28">
        <div className="space-y-8">
          <p className="text-ink-soft leading-relaxed max-w-md">
            {t("contact_intro", "For bespoke enquiries, styling appointments or press, our client care team is at your service.")}
          </p>
          <div className="space-y-6">
            {rows.map(([Icon, label, val], i) => (
              <FadeUp key={i} delay={i * 0.06}>
                <div className="flex items-start gap-4">
                  <div className="grid h-11 w-11 place-items-center border border-line text-gold-deep shrink-0"><Icon size={17} /></div>
                  <div>
                    <p className="overline text-ink-soft mb-1">{label}</p>
                    <p className="font-serif text-xl">{val}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
          <a href="https://wa.me/912240001200" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 btn-outline px-7 py-3.5 overline" data-testid="whatsapp-btn">
            <MessageCircle size={15} /> {t("contact_whatsapp_cta", "Chat on WhatsApp")}
          </a>
        </div>

        <FadeUp delay={0.1}>
          <form onSubmit={submit} className="bg-[#f2efe9] p-8 md:p-10 space-y-5" data-testid="contact-form">
            <h2 className="font-serif text-3xl mb-2">{t("contact_form_title", "Send a message")}</h2>
            <label className="block"><span className="overline text-ink-soft mb-1.5 block">Name</span><Input value={form.name} onChange={set("name")} data-testid="contact-name" /></label>
            <label className="block"><span className="overline text-ink-soft mb-1.5 block">Email</span><Input type="email" value={form.email} onChange={set("email")} data-testid="contact-email" /></label>
            <label className="block"><span className="overline text-ink-soft mb-1.5 block">Message</span><Textarea rows={5} value={form.message} onChange={set("message")} data-testid="contact-message" /></label>
            <button type="submit" className="btn-ink w-full py-4 overline" data-testid="contact-submit">Send message</button>
          </form>
        </FadeUp>
      </div>
    </div>
  );
}
